/**
 * JLC Cart Questions
 * ------------------
 * Web component that:
 *   1. Validates required cart questions (custom field types).
 *   2. Disables the Checkout button until all required fields are filled.
 *   3. Toggles body.jlc-cart-questions-pending so CSS can hide express checkout buttons.
 *   4. Inserts a helper notice above the Checkout button listing which fields are missing.
 *   5. Re-fetches the section on cart-updated events so tag-conditional blocks update
 *      live when items are added/removed without a page reload.
 *   6. Populates "date dropdown" selects with Today/Tomorrow/... options, honouring a
 *      same-day cutoff in a configurable IANA timezone (e.g. Asia/Dubai for UAE).
 */

if (!customElements.get('cart-questions')) {
  customElements.define(
    'cart-questions',
    class CartQuestions extends HTMLElement {
      connectedCallback() {
        this._onAnyChange = this._onAnyChange.bind(this);
        this._onCartUpdated = this._onCartUpdated.bind(this);
        this._onCartRefresh = this._onCartRefresh.bind(this);

        document.addEventListener('input', this._onAnyChange);
        document.addEventListener('change', this._onAnyChange);

        const cartForm = document.getElementById('AjaxCartForm');
        if (cartForm) cartForm.addEventListener('cart-updated', this._onCartUpdated);

        // Some sections (e.g. JLC Final Call Combined) replace the cart drawer's
        // innerHTML directly after their own /cart/add.js call — recreating
        // #CheckOut without firing 'cart-updated'. Watch ONLY for #CheckOut being
        // added/removed (NOT all subtree changes) — otherwise validate()'s own
        // DOM writes (inserting the notice element) would cause a feedback loop
        // and freeze the page.
        const drawer = document.getElementById('site-cart-sidebar');
        if (drawer) {
          this._drawerObserver = new MutationObserver((mutations) => {
            let checkoutChanged = false;
            for (const m of mutations) {
              for (const n of m.addedNodes) {
                if (n.nodeType !== 1) continue;
                if (n.id === 'CheckOut' || (n.querySelector && n.querySelector('#CheckOut'))) {
                  checkoutChanged = true;
                  break;
                }
              }
              if (checkoutChanged) break;
            }
            if (checkoutChanged) {
              queueMicrotask(() => this.validate());
            }
          });
          this._drawerObserver.observe(drawer, { childList: true, subtree: true });
        }

        // Some sections dispatch a 'cart:refresh' custom event on the document
        // after manually updating the cart. Listen for it as an additional hook.
        document.addEventListener('cart:refresh', this._onCartRefresh);

        this._applyDateBounds();
        this._populateDateDropdowns();
        this.validate();
      }

      disconnectedCallback() {
        document.removeEventListener('input', this._onAnyChange);
        document.removeEventListener('change', this._onAnyChange);
        document.removeEventListener('cart:refresh', this._onCartRefresh);
        const cartForm = document.getElementById('AjaxCartForm');
        if (cartForm) cartForm.removeEventListener('cart-updated', this._onCartUpdated);
        if (this._drawerObserver) {
          this._drawerObserver.disconnect();
          this._drawerObserver = null;
        }
      }

      _onCartRefresh() {
        // Defer slightly so any DOM swap that triggered the event has settled.
        setTimeout(() => this._onCartUpdated(), 50);
      }

      _onAnyChange() {
        // Defer one microtask so other handlers (terms-checkbox etc.) run first.
        queueMicrotask(() => this.validate());
      }

      async _onCartUpdated() {
        const sectionId = this.dataset.sectionId || 'jlc-cart-questions';
        try {
          const res = await fetch(`${window.location.pathname}?sections=${sectionId}`, {
            credentials: 'same-origin',
          });
          if (!res.ok) return;
          const data = await res.json();
          const html = data[sectionId];
          if (!html) return;

          const wrapper = document.createElement('div');
          wrapper.innerHTML = html.trim();
          const fresh = wrapper.querySelector('cart-questions');
          if (fresh) {
            this.innerHTML = fresh.innerHTML;
            this._applyDateBounds();
            this._populateDateDropdowns();
            this.validate();
          }
        } catch (_) {
          /* swallow */
        }
      }

      _applyDateBounds() {
        const today = new Date();
        const fmt = (d) => d.toISOString().slice(0, 10);
        this.querySelectorAll('input[type="date"]').forEach((input) => {
          const minOffset = parseInt(input.dataset.minOffset, 10);
          const maxOffset = parseInt(input.dataset.maxOffset, 10);
          if (!isNaN(minOffset)) {
            const d = new Date(today);
            d.setDate(today.getDate() + minOffset);
            input.min = fmt(d);
          }
          if (!isNaN(maxOffset)) {
            const d = new Date(today);
            d.setDate(today.getDate() + maxOffset);
            input.max = fmt(d);
          }
        });
      }

      // Return { year, month, day, hour, minute } for current time in a given IANA timezone.
      _tzNow(timezone) {
        try {
          const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: timezone,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
            hour12: false,
          }).formatToParts(new Date());
          const get = (t) => parts.find((p) => p.type === t).value;
          let hour = parseInt(get('hour'), 10);
          if (hour === 24) hour = 0; // Some engines emit 24 at midnight; normalize.
          return {
            year: parseInt(get('year'), 10),
            month: parseInt(get('month'), 10),
            day: parseInt(get('day'), 10),
            hour: hour,
            minute: parseInt(get('minute'), 10),
          };
        } catch (_) {
          // Fallback to user's local time if timezone is invalid.
          const d = new Date();
          return {
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
            hour: d.getHours(),
            minute: d.getMinutes(),
          };
        }
      }

      _populateDateDropdowns() {
        this.querySelectorAll('select[data-date-dropdown]').forEach((select) => {
          const minOffset = parseInt(select.dataset.minOffset, 10) || 0;
          const maxOffset = parseInt(select.dataset.maxOffset, 10) || 7;
          const cutoffHour = parseInt(select.dataset.cutoffHour, 10);
          const cutoffMinute = parseInt(select.dataset.cutoffMinute, 10) || 0;
          const timezone = select.dataset.timezone || 'Asia/Dubai';
          const placeholder = select.dataset.placeholder || 'Select a date';
          const locale = select.dataset.locale || 'en-GB';

          const tzNow = this._tzNow(timezone);
          const pastCutoff =
            !isNaN(cutoffHour) &&
            (tzNow.hour > cutoffHour ||
              (tzNow.hour === cutoffHour && tzNow.minute >= cutoffMinute));

          const currentValue = select.value;
          select.innerHTML = '';

          const phOpt = document.createElement('option');
          phOpt.value = '';
          phOpt.textContent = placeholder;
          select.appendChild(phOpt);

          for (let i = minOffset; i <= maxOffset; i++) {
            if (i === 0 && pastCutoff) continue;

            const d = new Date(tzNow.year, tzNow.month - 1, tzNow.day);
            d.setDate(d.getDate() + i);

            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const ymd = `${yyyy}-${mm}-${dd}`;

            const dayMonth = d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
            const weekday = d.toLocaleDateString(locale, { weekday: 'short' });

            let label;
            if (i === 0) label = `Today — ${dayMonth}`;
            else if (i === 1) label = `Tomorrow — ${dayMonth}`;
            else label = `${weekday} — ${dayMonth}`;

            const opt = document.createElement('option');
            opt.value = ymd;
            opt.textContent = label;
            select.appendChild(opt);
          }

          // Restore previous selection if still valid
          if (currentValue && Array.from(select.options).some((o) => o.value === currentValue)) {
            select.value = currentValue;
          }
        });
      }

      // Get the human label of a field for the "Please complete" notice.
      _labelFor(input) {
        const field = input.closest('.jlc-cart-questions__field');
        if (!field) return '';
        const labelEl = field.querySelector('label');
        if (!labelEl) return '';
        const clone = labelEl.cloneNode(true);
        // Strip the red asterisk so labels read cleanly in the notice.
        clone.querySelectorAll('.jlc-cart-questions__star').forEach((s) => s.remove());
        return clone.textContent.replace(/\s+/g, ' ').trim();
      }

      _renderNotice(missingFields) {
        const message = missingFields.length > 0;
        document.querySelectorAll('#CheckOut').forEach((btn) => {
          // Anchor: prefer .flex-buttons row so the notice sits above both view-cart + checkout.
          const anchor = btn.closest('.flex-buttons') || btn;
          const parent = anchor.parentNode;
          if (!parent) return;

          let notice = parent.querySelector(':scope > .jlc-cart-questions__notice');
          if (!notice) {
            if (!message) return; // nothing to show, nothing to create
            notice = document.createElement('div');
            notice.className = 'jlc-cart-questions__notice';
            parent.insertBefore(notice, anchor);
          }

          if (message) {
            // Use <strong> not <span> so the theme's `.cart__details > div > span { display: block }`
            // rule doesn't stack each field onto its own line.
            const list = missingFields
              .map((f) => `<strong class="jlc-cart-questions__notice-fields">${f}</strong>`)
              .join(', ');
            notice.innerHTML = `Please complete: ${list}`;
            notice.style.display = '';
          } else {
            notice.style.display = 'none';
          }
        });
      }

      validate() {
        const requiredInputs = this.querySelectorAll('[data-q-required]');
        const missing = [];

        requiredInputs.forEach((input) => {
          const field = input.closest('.jlc-cart-questions__field');
          let valid = true;

          if (input.type === 'checkbox') {
            valid = input.checked;
          } else {
            const value = (input.value || '').trim();
            if (!value) {
              valid = false;
            } else if (input.pattern) {
              try {
                valid = new RegExp(input.pattern).test(value);
              } catch (_) {
                /* invalid regex: skip */
              }
            }
          }

          if (field) {
            if (!valid && input.dataset.touched === 'true') {
              field.dataset.invalid = 'true';
            } else {
              field.removeAttribute('data-invalid');
            }
          }

          if (!valid) {
            const label = this._labelFor(input) || 'a required field';
            if (!missing.includes(label)) missing.push(label);
          }
        });

        // Include the theme's existing terms checkbox in the missing list and gating.
        const terms = document.getElementById('AgreeToTos');
        const termsValid = !terms || terms.checked;
        if (terms && !termsValid) {
          const termsLabel =
            terms.parentElement?.querySelector('label')?.textContent.trim().replace(/\s+/g, ' ') ||
            'Agree to terms';
          // Cap long terms text so the notice stays compact.
          const short = termsLabel.length > 40 ? termsLabel.slice(0, 40) + '…' : termsLabel;
          missing.push(short);
        }

        // Express checkout suppression is based ONLY on our required fields,
        // because the terms checkbox already has its own express-button behaviour.
        const ourRequiredValid = Array.from(requiredInputs).every((input) => {
          if (input.type === 'checkbox') return input.checked;
          const value = (input.value || '').trim();
          if (!value) return false;
          if (input.pattern) {
            try { return new RegExp(input.pattern).test(value); } catch (_) { return true; }
          }
          return true;
        });

        const allValid = missing.length === 0;

        document.querySelectorAll('#CheckOut').forEach((btn) => {
          btn.disabled = !allValid;
        });

        document.body.classList.toggle('jlc-cart-questions-pending', !ourRequiredValid);
        this._renderNotice(missing);
      }
    }
  );

  // Mark required fields as "touched" so the soft red border only appears after interaction.
  document.addEventListener(
    'blur',
    (e) => {
      const t = e.target;
      if (t && t.hasAttribute && t.hasAttribute('data-q-required')) {
        t.dataset.touched = 'true';
        const wrapper = t.closest('cart-questions');
        if (wrapper && typeof wrapper.validate === 'function') wrapper.validate();
      }
    },
    true
  );
}
